import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardOverviewData } from './useDashboardOverviewData'
import { PeriodDropdown } from './PeriodDropdown'
import { KpiCard } from './KpiCard'
import { SalesCard } from './SalesCard'
import { TeamSplitCard } from './TeamSplitCard'
import { SalesByMonthChart } from './SalesByMonthChart'
import { TotalsSection } from './TotalsSection'
import { ProductionSection } from './ProductionSection'
import { PortfolioHealthSection } from './PortfolioHealthSection'
import IconSpinner from '../../assets/icons/spinner.svg?react'

type DashboardOverviewProps = {
    setPath: (path: string) => void
}

export const DashboardOverview: React.FC<DashboardOverviewProps> = ({ setPath }) => {
    const { t } = useTranslation('dashboard')
    const {
        isLoading,
        periodSelector,
        flowKpis,
        salesCard,
        teamSplit,
        salesChart,
        totals,
        productionStages,
        productionChart,
        productionSummary,
        portfolioWidgets
    } = useDashboardOverviewData()

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <IconSpinner className="size-10" />
            </div>
        )
    }

    return (
        <div className="w-full h-full overflow-y-auto no-scrollbar">
            <div className="flex flex-col gap-7  pb-10">

                <h1 className="text-[26px] font-semibold tracking-tight text-neutralBlack-900">{t('overview')}</h1>

                <section className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="text-[17px] font-semibold tracking-tight text-neutralBlack-900">{t('metrics.periodActivity')}</h2>
                        <span className="flex-1 min-w-2" />
                        <PeriodDropdown {...periodSelector} />
                    </div>

                    <div className="grid gap-3 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(238px, 1fr))' }}>
                        {flowKpis.map((kpi) => (
                            <KpiCard key={kpi.key} label={kpi.label} value={kpi.value} unit={kpi.unit} note={kpi.note} markets={kpi.markets} />
                        ))}
                        <SalesCard {...salesCard} />
                        <TeamSplitCard {...teamSplit} />
                    </div>

                    <SalesByMonthChart average={salesChart.average} legend={salesChart.legend} months={salesChart.months} yAxisTicks={salesChart.yAxis.ticks} />
                </section>

                <ProductionSection summary={productionSummary} stages={productionStages} chart={productionChart} />

                <PortfolioHealthSection widgets={portfolioWidgets} />

                <TotalsSection {...totals} onViewAllAgencies={() => setPath('agencies')} />
            </div>
        </div>
    )
}
